(() => {
  if (window.TcPilotPurchaseGuard) return;

  const checkoutSelector = '[data-tc-pilot-dynamic-checkout]';
  const checkoutTemplateSelector = '[data-tc-pilot-dynamic-checkout-template]';
  const contractSelector = '[data-tc-product-contract]';
  const guardDisabledAttribute = 'data-tc-pilot-guard-disabled';
  const guardReadyAttribute = 'data-tc-pilot-purchase-guard-ready';
  const purchaseAllowedAttribute = 'data-tc-pilot-purchase-allowed';
  const validStates = new Set(['valid_assignment', 'valid_preview']);
  const runtimeStates = new WeakMap();

  const asNonEmptyString = (value) => (typeof value === 'string' && value.trim() !== '' ? value : null);

  const asOptionalString = (value) => (typeof value === 'string' ? value : null);

  const asFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const getContract = (productInfo) => {
    const node = productInfo?.querySelector(contractSelector);
    if (!node) return null;

    try {
      const contract = JSON.parse(node.textContent);
      if (!contract || typeof contract !== 'object' || Array.isArray(contract)) return null;
      if (!asNonEmptyString(contract.state) || !asNonEmptyString(contract.family)) return null;
      if (!asNonEmptyString(contract.productId) || !asNonEmptyString(contract.handle)) return null;
      if (!asNonEmptyString(contract.renderedSuffix) || !asNonEmptyString(contract.expectedSuffix)) return null;
      if (asOptionalString(contract.assignedSuffix) === null) return null;
      if (!Array.isArray(contract.allowedVariantIds) || !contract.variants || typeof contract.variants !== 'object') {
        return null;
      }

      const allowedVariantIds = contract.allowedVariantIds.map(String);
      if (
        allowedVariantIds.length === 0 ||
        new Set(allowedVariantIds).size !== allowedVariantIds.length ||
        allowedVariantIds.some((id) => id.trim() === '')
      ) {
        return null;
      }

      return { ...contract, allowedVariantIds };
    } catch (_error) {
      return null;
    }
  };

  const contractIdentityMatches = (productInfo, contract) => {
    if (!productInfo || !contract || !validStates.has(contract.state)) return false;

    const assignmentMatches =
      (contract.state === 'valid_assignment' && contract.assignedSuffix === contract.expectedSuffix) ||
      (contract.state === 'valid_preview' && contract.assignedSuffix === '');

    return (
      assignmentMatches &&
      contract.renderedSuffix === contract.expectedSuffix &&
      productInfo.dataset.tcContractState === contract.state &&
      productInfo.dataset.tcContractFamily === contract.family &&
      productInfo.dataset.tcContractProductId === contract.productId &&
      productInfo.dataset.tcContractHandle === contract.handle &&
      productInfo.dataset.tcContractRenderedSuffix === contract.renderedSuffix &&
      productInfo.dataset.tcContractAssignedSuffix === contract.assignedSuffix &&
      productInfo.dataset.tcContractExpectedSuffix === contract.expectedSuffix
    );
  };

  const getVariantRecord = (contract, variantId) => {
    const normalizedVariantId = asNonEmptyString(String(variantId || ''));
    if (!normalizedVariantId || !contract.allowedVariantIds.includes(normalizedVariantId)) return null;

    const record = contract.variants[normalizedVariantId];
    if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
    if (typeof record.available !== 'boolean') return null;

    const quantityRule = record.quantityRule;
    if (!quantityRule || typeof quantityRule !== 'object' || Array.isArray(quantityRule)) return null;

    const min = asFiniteNumber(quantityRule.min);
    const max = asFiniteNumber(quantityRule.max);
    const step = asFiniteNumber(quantityRule.step);
    if (!Number.isInteger(min) || min < 1 || !Number.isInteger(step) || step < 1) return null;
    if (max !== null && (!Number.isInteger(max) || max < min)) return null;

    return {
      available: record.available,
      quantityRule: { min, max, step },
    };
  };

  const getProductForms = (productInfo) => [...productInfo.querySelectorAll('form[action*="/cart/add"]')];

  const getPrimaryForm = (productInfo) =>
    productInfo.querySelector('product-form form[data-type="add-to-cart-form"]');

  const getPrimaryElements = (productInfo) => {
    const form = getPrimaryForm(productInfo);
    return {
      form,
      idInputs: form ? [...form.elements].filter((element) => element?.name === 'id') : [],
      quantityInputs: form ? [...form.elements].filter((element) => element?.name === 'quantity') : [],
      submitButton: form?.querySelector('button[name="add"]') || null,
      dynamicCheckout: productInfo.querySelector(checkoutSelector),
      dynamicCheckoutTemplate: productInfo.querySelector(checkoutTemplateSelector),
    };
  };

  const isGuardOwnedDisabled = (element) => element?.getAttribute(guardDisabledAttribute) === 'true';

  const isNativelyDisabled = (element) => Boolean(element?.disabled) && !isGuardOwnedDisabled(element);

  const getUsablePrimaryIdInputs = (idInputs) =>
    idInputs.filter((input) => !input.disabled || isGuardOwnedDisabled(input));

  const allPurchaseFormsCoherent = (productInfo, primaryForm, expectedVariantId) =>
    getProductForms(productInfo).every((form) => {
      const activeIds = [...form.elements].filter((element) => element?.name === 'id' && !element.disabled);
      if (form === primaryForm) return activeIds.length === 1 && activeIds[0].value === expectedVariantId;
      return activeIds.every((input) => input.value === expectedVariantId);
    });

  const quantityIsValid = (quantityInputs, quantityRule) => {
    const activeInputs = quantityInputs.filter((input) => !input.disabled);
    if (activeInputs.length !== 1) return false;

    const input = activeInputs[0];
    const quantity = asFiniteNumber(input.value);
    const domMin = asFiniteNumber(input.min);
    const domMax = asFiniteNumber(input.max);
    const domStep = asFiniteNumber(input.step);

    if (!Number.isInteger(quantity) || quantity < 1) return false;
    if (domMin !== quantityRule.min || domMax !== quantityRule.max || domStep !== quantityRule.step) return false;
    if (quantity < quantityRule.min || (quantityRule.max !== null && quantity > quantityRule.max)) return false;

    return (quantity - quantityRule.min) % quantityRule.step === 0;
  };

  const validate = (productInfo, options = {}) => {
    const contract = getContract(productInfo);
    if (!contractIdentityMatches(productInfo, contract)) return false;

    const runtimeState = runtimeStates.get(productInfo);
    const expectedVariantId = String(options.expectedVariantId || runtimeState?.variantId || '');
    const variantRecord = getVariantRecord(contract, expectedVariantId);
    if (!variantRecord) return false;

    const availability =
      typeof options.availability === 'boolean'
        ? options.availability
        : runtimeState?.variantId === expectedVariantId
          ? runtimeState.availability
          : variantRecord.available;
    if (availability !== true) return false;

    const { form, idInputs, quantityInputs, submitButton } = getPrimaryElements(productInfo);
    if (!form || !submitButton) return false;

    const candidateIds = getUsablePrimaryIdInputs(idInputs);
    if (candidateIds.length !== 1 || candidateIds[0].value !== expectedVariantId) return false;
    if (isNativelyDisabled(candidateIds[0])) return false;

    const shopifySubmitDisabled =
      typeof options.shopifySubmitDisabled === 'boolean'
        ? options.shopifySubmitDisabled
        : runtimeState?.shopifySubmitDisabled;
    if (shopifySubmitDisabled === true || isNativelyDisabled(submitButton)) return false;
    if (!quantityIsValid(quantityInputs, variantRecord.quantityRule)) return false;

    if (options.submittedForm) {
      if (options.submittedForm !== form) return false;
      const activeSubmittedIds = [...form.elements].filter(
        (element) => element?.name === 'id' && !element.disabled
      );
      if (activeSubmittedIds.length !== 1 || activeSubmittedIds[0].value !== expectedVariantId) return false;
      if (!allPurchaseFormsCoherent(productInfo, form, expectedVariantId)) return false;
    } else {
      const otherFormsCoherent = getProductForms(productInfo)
        .filter((purchaseForm) => purchaseForm !== form)
        .every((purchaseForm) =>
          [...purchaseForm.elements]
            .filter((element) => element?.name === 'id' && !element.disabled)
            .every((input) => input.value === expectedVariantId)
        );
      if (!otherFormsCoherent) return false;
    }

    return true;
  };

  const closeDynamicCheckout = (dynamicCheckout) => {
    if (!dynamicCheckout) return;
    dynamicCheckout.replaceChildren();
    dynamicCheckout.hidden = true;
    dynamicCheckout.dataset.available = 'false';
  };

  const openDynamicCheckout = (productInfo, dynamicCheckout, dynamicCheckoutTemplate) => {
    if (!dynamicCheckout) return;
    if (!validate(productInfo)) {
      closeDynamicCheckout(dynamicCheckout);
      return;
    }

    if (!dynamicCheckoutTemplate || dynamicCheckoutTemplate.content.childElementCount === 0) {
      closeDynamicCheckout(dynamicCheckout);
      return;
    }

    if (dynamicCheckout.childElementCount === 0) {
      dynamicCheckout.append(dynamicCheckoutTemplate.content.cloneNode(true));
      if (dynamicCheckout.dataset.tcPilotPaymentInitialized !== 'true') {
        window.Shopify?.PaymentButton?.init();
        dynamicCheckout.dataset.tcPilotPaymentInitialized = 'true';
      }
    }

    dynamicCheckout.hidden = false;
    dynamicCheckout.dataset.available = 'true';
  };

  const setGuardOwnedDisabled = (element, disabled) => {
    if (!element) return;

    if (disabled) {
      if (!element.disabled) {
        element.disabled = true;
        element.setAttribute(guardDisabledAttribute, 'true');
      } else if (!isGuardOwnedDisabled(element)) {
        element.removeAttribute(guardDisabledAttribute);
      }
      return;
    }

    if (isGuardOwnedDisabled(element)) {
      element.disabled = false;
      element.removeAttribute(guardDisabledAttribute);
    }
  };

  const synchronizeFetchedControlState = (currentElement, fetchedElement) => {
    if (!currentElement) return;

    const currentLabel = currentElement.querySelector?.('span');
    const fetchedLabel = fetchedElement?.querySelector?.('span')?.textContent?.trim();
    if (currentLabel && fetchedLabel) currentLabel.textContent = fetchedLabel;

    const fetchedIsGuardOwned =
      fetchedElement?.disabled === true && fetchedElement.getAttribute(guardDisabledAttribute) === 'true';
    const fetchedIsNativelyDisabled =
      !fetchedElement || (fetchedElement.disabled === true && !fetchedIsGuardOwned);

    currentElement.disabled = true;
    if (!fetchedIsNativelyDisabled) {
      currentElement.setAttribute(guardDisabledAttribute, 'true');
    } else {
      currentElement.removeAttribute(guardDisabledAttribute);
    }

    return fetchedIsNativelyDisabled;
  };

  const applyState = (productInfo, purchaseAllowed) => {
    const { idInputs, submitButton, dynamicCheckout, dynamicCheckoutTemplate } = getPrimaryElements(productInfo);
    productInfo.setAttribute(purchaseAllowedAttribute, String(purchaseAllowed));

    idInputs.forEach((input) => setGuardOwnedDisabled(input, !purchaseAllowed));
    setGuardOwnedDisabled(submitButton, !purchaseAllowed);

    if (purchaseAllowed) {
      openDynamicCheckout(productInfo, dynamicCheckout, dynamicCheckoutTemplate);
    } else {
      closeDynamicCheckout(dynamicCheckout);
    }
  };

  const failClosed = (productInfo) => {
    if (!productInfo) return false;
    const previousState = runtimeStates.get(productInfo);
    runtimeStates.set(productInfo, {
      variantId: previousState?.variantId || '',
      availability: false,
      shopifySubmitDisabled: previousState?.shopifySubmitDisabled,
    });
    applyState(productInfo, false);
    return false;
  };

  const update = (productInfo, availability, shopifySubmitDisabled, expectedVariantId) => {
    if (!productInfo) return false;

    const normalizedVariantId = String(expectedVariantId || '');
    runtimeStates.set(productInfo, {
      variantId: normalizedVariantId,
      availability: typeof availability === 'boolean' ? availability : null,
      shopifySubmitDisabled:
        typeof shopifySubmitDisabled === 'boolean' ? shopifySubmitDisabled : undefined,
    });

    const purchaseAllowed =
      availability === true &&
      validate(productInfo, {
        expectedVariantId: normalizedVariantId,
        availability,
        shopifySubmitDisabled,
      });
    applyState(productInfo, purchaseAllowed);
    return purchaseAllowed;
  };

  const installGuards = (productInfo) => {
    if (productInfo.getAttribute(guardReadyAttribute) === 'true') return;
    productInfo.setAttribute(guardReadyAttribute, 'true');

    productInfo.addEventListener(
      'submit',
      (event) => {
        if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-type="add-to-cart-form"]')) {
          return;
        }

        const allowed = validate(productInfo, { submittedForm: event.target });
        applyState(productInfo, allowed);
        if (allowed) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    ['pointerdown', 'mousedown', 'touchstart', 'click', 'keydown'].forEach((eventName) => {
      productInfo.addEventListener(
        eventName,
        (event) => {
          if (!event.target.closest?.(checkoutSelector)) return;
          const allowed = validate(productInfo);
          applyState(productInfo, allowed);
          if (allowed) return;

          event.preventDefault();
          event.stopImmediatePropagation();
        },
        true
      );
    });
  };

  const initialize = (productInfo) => {
    installGuards(productInfo);
    failClosed(productInfo);

    const contract = getContract(productInfo);
    const selectedVariant = contract?.selectedVariant;
    if (!selectedVariant || typeof selectedVariant !== 'object') return false;

    return update(productInfo, selectedVariant.available, undefined, selectedVariant.id);
  };

  window.TcPilotPurchaseGuard = Object.freeze({
    failClosed,
    initialize,
    update,
    validate,
  });

  const guardedProductInfos = [...document.querySelectorAll('product-info[data-tc-pilot-guard-view]')];
  guardedProductInfos.forEach(initialize);

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
    const productInfo = event?.data?.event?.target?.closest('product-info[data-tc-pilot-guard-view]');
    if (productInfo) failClosed(productInfo);
  });

  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const sectionId = event?.data?.sectionId;
    const variant = event?.data?.variant;
    if (!sectionId || !variant) return;

    const productInfo = document.getElementById(`MainProduct-${sectionId}`);
    if (!productInfo?.matches('product-info[data-tc-pilot-guard-view]')) return;

    const fetchedSubmitButton = event?.data?.html?.getElementById(`ProductSubmitButton-${sectionId}`);
    const fetchedVariantInput = event?.data?.html?.querySelector(
      `#product-form-${sectionId} input[name="id"]`
    );
    const { idInputs, submitButton } = getPrimaryElements(productInfo);
    const shopifySubmitDisabled = synchronizeFetchedControlState(submitButton, fetchedSubmitButton);
    idInputs.forEach((input) => synchronizeFetchedControlState(input, fetchedVariantInput));
    update(productInfo, variant.available, shopifySubmitDisabled, variant.id);
  });
})();
